// client/src/pages/Admin/ProjectManager/hooks/useDebounce.ts
/**
 * USE DEBOUNCE HOOK
 * 
 * This hook debounces a value, delaying updates until after a specified delay.
 * Useful for search inputs and other frequent updates.
 * 
 * @module ProjectManager/Hooks/useDebounce
 */

import { useState, useEffect } from 'react';
import { DEBOUNCE_DELAY } from '../constants/project.constants';

/**
 * useDebounce hook
 * 
 * @param value - Value to debounce
 * @param delay - Delay in milliseconds (default: 300ms)
 * @returns Debounced value
 * 
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearch = useDebounce(searchTerm, 300);
 */
export const useDebounce = <T>(
    value: T,
    delay: number = DEBOUNCE_DELAY
): T => {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
};